import React from 'react';

interface UpgradePromptProps {
  featureName: string;
  description: string;
  onUpgrade?: () => void;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  featureName,
  description,
  onUpgrade
}) => {
  return (
    <div className="relative rounded-lg shadow-xl overflow-hidden bg-gray-800/50 border border-gray-700">
      {/* Top accent line */}
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, #f59e0b 15%, #f59e0b 85%, transparent 100%)'
        }}
      />

      <div className="p-6">
        <div className="flex items-start gap-4">
          {/* Lock icon */}
          <div className="flex-shrink-0 p-3 rounded-xl bg-amber-500/20">
            <svg
              className="w-6 h-6 text-amber-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-1">
              {featureName}
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              {description}
            </p>

            <div className="flex items-center gap-3">
              <button
                onClick={onUpgrade}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-white font-medium text-sm hover:from-amber-600 hover:to-amber-700 transition-all duration-200 shadow-lg shadow-amber-500/25"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
                Upgrade Plan
              </button>

              <span className="text-xs text-gray-500">
                Available on Business & Enterprise plans
              </span>
            </div>
          </div>
        </div>

        {/* Feature highlights */}
        <div className="mt-6 pt-4 border-t border-gray-700/50">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">
            What you'll get:
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <li className="flex items-center text-sm text-gray-400">
              <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Upload all entity files in one ZIP
            </li>
            <li className="flex items-center text-sm text-gray-400">
              <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Auto-route files to correct offices
            </li>
            <li className="flex items-center text-sm text-gray-400">
              <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Track run completion across entities
            </li>
            <li className="flex items-center text-sm text-gray-400">
              <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Visual entity status dashboard
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;
